<template>
  <div class="event-balance-card p-3 p-sm-4">
    <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <div class="event-icon-wrapper">
          <i class="fas fa-gift"></i>
        </div>
        <div>
          <p class="event-name mb-0 fw-semibold">{{ item.event_name }}</p>
          <span class="badge" :class="eventStatusBadgeClass">{{ eventStatusLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Seção PIX -->
    <template v-if="availablePix > 0 || pendingAmountPix > 0 || recoveryPix > 0 || pixActiveRequestStatus">
      <div class="gateway-section mb-3">
        <div class="gateway-section__header mb-2">
          <i class="fas fa-qrcode me-1" style="color:#32bcad;font-size:0.8rem;"></i>
          <span class="gateway-section__label">Resgate via Pix</span>
        </div>

        <!-- Estado: transferência em andamento -->
        <template v-if="pixActiveRequestStatus">

          <!-- Saldo pendente D+3: destaque principal (igual ao estado sem transferência) -->
          <template v-if="pendingAmountPix > 0">
            <div class="amounts-row mb-2">
              <div class="amount-block">
                <p class="amount-label text-muted mb-1">A liberar em breve</p>
                <p class="amount-value fw-bold mb-0 text-secondary">{{ formatCurrency(pendingAmountPix) }}</p>
                <p v-if="availableAtFormatted" class="amount-sublabel text-muted mb-0">até {{ availableAtFormatted }}</p>
              </div>
            </div>
            <div class="pending-notice d-flex align-items-start gap-2 p-2 rounded-3 mb-2">
              <i class="fas fa-clock text-warning mt-1" style="font-size:0.85rem;flex-shrink:0;"></i>
              <span style="font-size:0.82rem;color:#664d03;">
                <template v-if="isEventFinished">
                  Liberação automática prevista para {{ availableAtFormatted || 'em breve' }}. Após a liberação, a transferência para sua conta Pix ocorrerá automaticamente. Taxa: {{ formatCurrency(fixedFee) }}.
                </template>
                <template v-else>
                  Este valor só será transferido automaticamente <strong>após o encerramento do evento</strong>.
                </template>
              </span>
            </div>
          </template>

          <!-- Saldo disponível extra além do que foi transferido -->
          <div v-if="newAvailableAmount > 0" class="amount-block mb-2">
            <p class="amount-label text-muted mb-1">Disponível agora</p>
            <p class="amount-value fw-bold mb-0 text-success">{{ formatCurrency(newAvailableAmount) }}</p>
          </div>
          <template v-if="newAvailableAmount > 0 && !isEventFinished">
            <div class="pix-action-block__auto-hint mb-2">
              <i class="fas fa-clock me-1" style="font-size:0.7rem;"></i>
              Aguarde a transferência atual terminar para solicitar um novo resgate.
            </div>
          </template>

          <!-- Status da transferência ativa: secundário, abaixo -->
          <div class="transfer-in-progress-row" :class="pendingAmountPix > 0 ? 'transfer-in-progress-row--separator' : 'mb-2'">
            <div class="transfer-in-progress-row__info">
              <i :class="pixRequestStatusIcon" style="font-size:0.75rem;" class="me-1"></i>
              <span class="transfer-in-progress-row__label">{{ transferRowLabel }}</span>
            </div>
            <span class="transfer-in-progress-row__amount">{{ formatCurrency(requestedAmount) }}</span>
          </div>
          <div class="request-status-badge d-flex align-items-center gap-2 p-2 rounded-3">
            <span style="font-size:0.82rem;">{{ pixRequestStatusLabel }}</span>
          </div>
        </template>

        <!-- Estado: disponível + evento ativo — resgate antecipado ou aguardar encerramento -->
        <template v-else-if="availablePix > 0 && !isEventFinished">
          <template v-if="pendingAmountPix === 0">
            <div class="pix-action-block mb-3">
              <div class="pix-action-block__amount">{{ formatCurrency(availablePix) }}</div>
            </div>
          </template>
          <template v-else>
            <div class="amounts-row mb-3">
              <div class="amount-block">
                <p class="amount-label text-muted mb-1">Disponível agora</p>
                <p class="amount-value fw-bold mb-0 text-success">{{ formatCurrency(availablePix) }}</p>
              </div>
              <div class="amount-block">
                <p class="amount-label text-muted mb-1">A liberar em breve</p>
                <p class="amount-value fw-bold mb-0 text-secondary">{{ formatCurrency(pendingAmountPix) }}</p>
                <p v-if="availableAtFormatted" class="amount-sublabel text-muted mb-0">até {{ availableAtFormatted }}</p>
              </div>
            </div>
          </template>
          <div class="pix-action-block__auto-hint mb-2">
            <i class="fas fa-clock me-1" style="font-size:0.7rem;"></i>
            Será transferido automaticamente após o encerramento do evento. Taxa: {{ formatCurrency(fixedFee) }}.
          </div>
          <button
            class="btn btn-pix-primary w-100"
            :disabled="requesting"
            @click="$emit('request-withdrawal', item.event_id, 'full')"
          >
            <span v-if="requesting" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
            Solicitar resgate Pix
          </button>
        </template>

        <!-- Estado: disponível + evento encerrado — transferência automática em andamento -->
        <template v-else-if="availablePix > 0 && isEventFinished">
          <div class="pix-action-block">
            <div class="pix-action-block__amount">{{ formatCurrency(availablePix) }}</div>
            <div class="pix-action-block__auto-hint pix-action-block__auto-hint--primary">
              <i class="fas fa-clock me-1" style="font-size:0.7rem;"></i>
              Transferência automática em andamento — o valor chegará em sua conta em até 1 hora. Taxa: {{ formatCurrency(fixedFee) }}.
            </div>
          </div>
          <template v-if="pendingAmountPix > 0">
            <div class="amounts-row mt-3">
              <div class="amount-block">
                <p class="amount-label text-muted mb-1">A liberar em breve</p>
                <p class="amount-value fw-bold mb-0 text-secondary">{{ formatCurrency(pendingAmountPix) }}</p>
                <p v-if="availableAtFormatted" class="amount-sublabel text-muted mb-0">até {{ availableAtFormatted }}</p>
              </div>
            </div>
          </template>
        </template>

        <!-- Estado: somente saldo pendente (D+3 ainda não liberou) -->
        <template v-else-if="pendingAmountPix > 0">
          <div class="amounts-row mb-2">
            <div class="amount-block">
              <p class="amount-label text-muted mb-1">A liberar em breve</p>
              <p class="amount-value fw-bold mb-0 text-secondary">{{ formatCurrency(pendingAmountPix) }}</p>
              <p v-if="availableAtFormatted" class="amount-sublabel text-muted mb-0">até {{ availableAtFormatted }}</p>
            </div>
          </div>
          <div class="pending-notice d-flex align-items-start gap-2 p-2 rounded-3 mb-2">
            <i class="fas fa-clock text-warning mt-1" style="font-size:0.85rem;flex-shrink:0;"></i>
            <span style="font-size:0.82rem;color:#664d03;">
              <template v-if="isEventFinished">
                Liberação automática prevista para {{ availableAtFormatted || 'em breve' }}. Após a liberação, a transferência para sua conta Pix ocorrerá automaticamente. Taxa: {{ formatCurrency(fixedFee) }}.
              </template>
              <template v-else>
                Este valor só será transferido automaticamente <strong>após o encerramento do evento</strong>.
                Se precisar antes, use o saque antecipado abaixo.
              </template>
            </span>
          </div>
          <button
            class="btn btn-early-withdrawal btn-early-withdrawal--primary w-100"
            :disabled="requestingEarly"
            @click="$emit('early-pix-withdrawal', item.event_id)"
          >
            <span v-if="requestingEarly" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
            Adiantar resgate (taxa: {{ formatCurrency(earlyFee) }})
          </button>
        </template>

        <!-- Aviso sutil de falha — retentativa somente via aba Avisos -->
        <div v-if="recoveryPix > 0" class="recovery-notice mt-2">
          <i class="fas fa-circle-info me-1" style="font-size:0.75rem;"></i>
          <span>Há uma ou mais transferências com falhas totalizando ({{ formatCurrency(recoveryPix) }}) — nossa equipe está ciente e trabalhando para resolver.</span>
        </div>
      </div>
    </template>

    <!-- Seção Cartão -->
    <template v-if="availableCard > 0 || pendingAmountCard > 0 || paidOutCard > 0">
      <div class="gateway-section gateway-section--card">
        <div class="gateway-section__header mb-2">
          <i class="fas fa-credit-card me-1" style="color:#635bff;font-size:0.8rem;"></i>
          <span class="gateway-section__label">Repasse por cartão</span>
        </div>

        <!-- Valor disponível aguardando repasse Stripe -->
        <template v-if="availableCard > 0">
          <div class="card-info-block mb-2">
            <div class="card-info-block__amount">{{ formatCurrency(availableCard) }}</div>
            <div class="card-info-block__notice d-flex align-items-start gap-2 p-2 rounded-3">
              <i class="fas fa-calendar-check mt-1" style="color:#635bff;font-size:0.8rem;flex-shrink:0;"></i>
              <span style="font-size:0.82rem;" class="text-muted">
                Depósito previsto para {{ cardPayoutDateFormatted }}.
                Enviado automaticamente para sua conta bancária, sem custo adicional.
              </span>
            </div>
          </div>
        </template>

        <!-- Valor ainda bloqueado D+3 para cartão -->
        <template v-if="pendingAmountCard > 0">
          <div class="card-pending-block">
            <div class="d-flex align-items-baseline gap-2 mb-1">
              <span class="card-pending-block__label text-muted">A liberar em breve</span>
            </div>
            <div class="card-pending-block__amount mb-2">{{ formatCurrency(pendingAmountCard) }}</div>
            <div class="card-pending-notice d-flex align-items-start gap-2 p-2 rounded-3">
              <i class="fas fa-info-circle mt-1" style="color:#635bff;font-size:0.8rem;flex-shrink:0;"></i>
              <span style="font-size:0.82rem;color:#3730a3;">
                Liberação automática prevista para {{ availableAtMaxCardFormatted || 'em breve' }}.
                Enviado automaticamente para sua conta bancária, sem custo. Não é possível antecipar pagamentos de cartão.
              </span>
            </div>
          </div>
        </template>

        <template v-if="paidOutCard > 0">
          <div class="card-info-block mt-2">
            <div class="card-info-block__amount">{{ formatCurrency(paidOutCard) }}</div>
            <div class="card-info-block__notice card-info-block__notice--paid d-flex align-items-start gap-2 p-2 rounded-3">
              <i class="fas fa-circle-check mt-1" style="color:#15803d;font-size:0.8rem;flex-shrink:0;"></i>
              <span style="font-size:0.82rem;" class="text-muted">
                Valor enviado para sua conta bancária.
              </span>
            </div>
          </div>
        </template>
      </div>
    </template>

    <div v-if="!hasFinancialMovement" class="empty-finance-state">
      <i class="fas fa-circle-info me-1" aria-hidden="true"></i>
      <span>Sem movimentações financeiras neste evento.</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { toFiniteNumber } from '@/utils/financeBuckets';
import {
  DEFAULT_EARLY_WITHDRAWAL_FEE_PERCENT,
  DEFAULT_TRANSFER_FEE_FIXED,
  CARD_PAYOUT_DELAY_DAYS,
} from '@/config/financeConfig';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  requesting: {
    type: Boolean,
    default: false,
  },
  requestingEarly: {
    type: Boolean,
    default: false,
  },
  earlyFeePercent: {
    type: Number,
    default: DEFAULT_EARLY_WITHDRAWAL_FEE_PERCENT,
  },
  fixedFee: {
    type: Number,
    default: DEFAULT_TRANSFER_FEE_FIXED,
  },
});

defineEmits(['request-withdrawal', 'early-pix-withdrawal']);

const hasExplicitPix = computed(() => Boolean(props.item.pix));
const hasExplicitCard = computed(() => Boolean(props.item.card));

const availablePix = computed(() => hasExplicitPix.value
  ? toFiniteNumber(props.item.pix?.available_to_withdraw)
  : toFiniteNumber(props.item.available_amount_pix));
const pendingAmountPix = computed(() => hasExplicitPix.value
  ? toFiniteNumber(props.item.pix?.pending_release)
  : toFiniteNumber(props.item.pending_amount_pix));
const recoveryPix = computed(() => hasExplicitPix.value
  ? toFiniteNumber(props.item.pix?.failed_recovered)
  : toFiniteNumber(props.item.recovery_amount_pix));
const availableCard = computed(() => hasExplicitCard.value
  ? toFiniteNumber(props.item.card?.pending_payout)
  : toFiniteNumber(props.item.available_amount_card));
const pendingAmountCard = computed(() => hasExplicitCard.value
  ? toFiniteNumber(props.item.card?.pending_release)
  : toFiniteNumber(props.item.pending_amount_card));
const paidOutCard = computed(() => hasExplicitCard.value
  ? toFiniteNumber(props.item.card?.paid_out)
  : 0);

// earlyFee = R$5 + 2% do total pix (disponível + pendente)
const earlyFee = computed(() => {
  const total = availablePix.value + pendingAmountPix.value;
  const percent = Math.round(total * props.earlyFeePercent * 100) / 100;
  return props.fixedFee + percent;
});

const availableAtFormatted = computed(() => {
  const value = props.item.pix?.available_at_max || props.item.available_at_max;
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
});

const cardPayoutDateFormatted = computed(() => {
  if (props.item.card?.next_expected_payout_at) {
    const expected = new Date(props.item.card.next_expected_payout_at);
    if (!isNaN(expected)) {
      return expected.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  const d = new Date();
  d.setDate(d.getDate() + CARD_PAYOUT_DELAY_DAYS);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
});

const availableAtMaxCardFormatted = computed(() => {
  const value = props.item.card?.available_at_max
    || props.item.card?.next_expected_payout_at
    || props.item.available_at_max_card;
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
});

const pixActiveRequestStatus = computed(() => {
  const s = props.item.pix?.active_request_status || props.item.withdrawal_request_status;
  if (!s) return null;
  if (['pending', 'approved', 'processing'].includes(s)) return s;
  return null;
});

const hasFinancialMovement = computed(() =>
  availablePix.value > 0
  || pendingAmountPix.value > 0
  || recoveryPix.value > 0
  || Boolean(pixActiveRequestStatus.value)
  || availableCard.value > 0
  || pendingAmountCard.value > 0
  || paidOutCard.value > 0
);

const pixRequestStatusLabel = computed(() => {
  switch (pixActiveRequestStatus.value) {
    case 'pending': return 'Estamos processando sua transferência. Em breve o valor estará em sua conta.';
    case 'approved': return 'Transferência aprovada. O valor será enviado em instantes.';
    case 'processing': return 'Transferência em andamento. Em breve o valor estará em sua conta.';
    case 'failed': return 'Transferência falhou — nossa equipe está ciente e trabalhando para resolver.';
    case 'cancelled': return 'Transferência cancelada — nossa equipe está ciente e trabalhando para resolver.';
    default: return '';
  }
});

const pixRequestStatusIcon = computed(() => {
  switch (pixActiveRequestStatus.value) {
    case 'pending': return 'fas fa-hourglass-half text-warning';
    case 'approved': return 'fas fa-check-circle text-info';
    case 'processing': return 'fas fa-spinner fa-spin text-info';
    case 'failed': return 'fas fa-times-circle text-danger';
    case 'cancelled': return 'fas fa-ban text-secondary';
    default: return '';
  }
});

const transferRowLabel = computed(() =>
  ['failed', 'cancelled'].includes(pixActiveRequestStatus.value) ? 'Transferência' : 'Em transferência'
);

const requestedAmount = computed(() =>
  hasExplicitPix.value
    ? toFiniteNumber(props.item.pix?.in_transfer, availablePix.value)
    : toFiniteNumber(props.item.withdrawal_request_amount, availablePix.value)
);

const newAvailableAmount = computed(() => {
  if (!pixActiveRequestStatus.value) return 0;
  if (hasExplicitPix.value) return availablePix.value;
  const surplus = availablePix.value - requestedAmount.value;
  return surplus > 0 ? surplus : 0;
});

// Estado e label do evento
const eventStatusLabel = computed(() => {
  switch (props.item.event_status) {
    case 'in-progress': return 'Ativo';
    case 'finished':    return 'Encerrado';
    case 'processing':  return 'Processando';
    case 'completed':   return 'Concluído';
    case 'draft':       return 'Rascunho';
    default:            return props.item.event_status ?? 'Ativo';
  }
});

const eventStatusBadgeClass = computed(() => {
  switch (props.item.event_status) {
    case 'in-progress': return 'badge-active';
    case 'finished':    return 'badge-finished';
    case 'processing':  return 'badge-processing';
    case 'completed':   return 'badge-completed';
    default:            return 'badge-finished';
  }
});

const isEventFinished = computed(() => props.item.event_status === 'finished');

</script>

<style scoped>
.event-balance-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  border-left: 4px solid #28a745;
}

.event-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background-color: #e8f5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #28a745;
  font-size: 1rem;
  flex-shrink: 0;
}

.event-name {
  font-size: 0.95rem;
  line-height: 1.2;
}

.badge-finished {
  background-color: #f0f4f8;
  color: #6c757d;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 20px;
}

.badge-active {
  background-color: #dcfce7;
  color: #15803d;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 20px;
}

.badge-processing {
  background-color: #dbeafe;
  color: #1d4ed8;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 20px;
}

.badge-completed {
  background-color: #f0fdf4;
  color: #166534;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 20px;
}

.amounts-row {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.amount-block {
  flex: 1;
  min-width: 120px;
}

.amount-label {
  font-size: 0.78rem;
}

.amount-value {
  font-size: 1.15rem;
}

.amount-sublabel {
  font-size: 0.72rem;
}

.fee-row {
  border-top: 1px solid #f0f0f0;
  padding-top: 0.5rem;
}

.fee-label,
.fee-value {
  font-size: 0.82rem;
}

.net-row {
  border-top: 1px solid #f0f0f0;
  padding-top: 0.5rem;
}

.net-label {
  font-size: 0.9rem;
}

.net-value {
  font-size: 1.1rem;
}

/* Bloco de ação PIX (estado: disponível, sem pendências) */
.pix-action-block {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.pix-action-block__amount {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.1;
}

.pix-action-block__fee {
  font-size: 0.82rem;
  color: #6c757d;
  text-align: center;
}

.pix-action-block__fee-detail {
  color: #adb5bd;
}

.pix-action-block__auto-hint {
  font-size: 0.76rem;
  color: #adb5bd;
  text-align: center;
}

.pix-action-block__auto-hint--primary {
  font-size: 0.82rem;
  color: #155724;
  background: #d4edda;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  text-align: center;
}

/* Botão primário Pix */
.btn-pix-primary {
  background-color: #28a745;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  transition: background-color 0.2s;
}

.btn-pix-primary:hover:not(:disabled) {
  background-color: #218838;
  color: #fff;
}

.btn-pix-primary:disabled {
  opacity: 0.65;
}

.btn-early-withdrawal {
  background-color: #fff;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-radius: 10px;
  padding: 0.4rem 1rem;
  font-size: 0.82rem;
  font-weight: 500;
  transition: background-color 0.2s, color 0.2s;
}

.btn-early-withdrawal:hover:not(:disabled) {
  background-color: #f8f9fa;
  color: #495057;
}

.btn-early-withdrawal:disabled {
  opacity: 0.65;
}

/* Quando saque antecipado é a única opção */
.btn-early-withdrawal--primary {
  background-color: #28a745;
  color: #fff;
  border-color: #28a745;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
}

.btn-early-withdrawal--primary:hover:not(:disabled) {
  background-color: #218838;
  color: #fff;
}

.gateway-section {
  border-top: 1px solid #f0f0f0;
  padding-top: 0.75rem;
}

.gateway-section:first-child {
  border-top: none;
  padding-top: 0;
}

.gateway-section--card {
  margin-top: 0.5rem;
}

.gateway-section__header {
  display: flex;
  align-items: center;
}

.gateway-section__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
}

/* Bloco informativo do cartão */
.card-info-block {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.card-info-block__amount {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
}

.card-info-block__notice {
  background-color: #f5f3ff;
  border: 1px solid #e0d9ff;
}

.card-info-block__notice--paid {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
}

/* Cartão pendente D+3 */
.card-pending-block__label {
  font-size: 0.78rem;
}

.card-pending-block__amount {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
}

.card-pending-notice {
  background-color: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
}

.pending-notice {
  background-color: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
}

.recovery-notice {
  font-size: 0.78rem;
  color: #6c757d;
  padding: 0.3rem 0.1rem 0;
}

.empty-finance-state {
  border-top: 1px solid #f0f0f0;
  padding-top: 0.75rem;
  font-size: 0.82rem;
  color: #6c757d;
}

.request-status-badge {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  font-size: 0.82rem;
  color: #555;
}

/* Linha compacta de "Em transferência" dentro do bloco PIX ativo */
.transfer-in-progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.transfer-in-progress-row__info {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: #6b7280;
}

.transfer-in-progress-row__label {
  font-size: 0.78rem;
}

.transfer-in-progress-row__amount {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6b7280;
}

/* Separador quando o status de transferência vem abaixo do bloco principal */
.transfer-in-progress-row--separator {
  border-top: 1px solid #f0f0f0;
  padding-top: 0.75rem;
  margin-bottom: 0.5rem;
}

</style>

